/**
 * Which screen the address bar names, and moving between them.
 *
 * A dozen paths do not need a routing library: `screenAt()` is a pure function
 * from a path to a screen (tested), `useLocation()` keeps a component in step
 * with the history, and `go()` is the one way to move. Links stay ordinary
 * `<a href>`s, so a middle-click still opens a tab and a mailed link lands on
 * the same screen.
 */

import { useEffect, useState, type MouseEvent } from "react";
import type { LifecycleKind } from "@utuwrk/core";

export type Screen =
  | { name: "queue" }
  | { name: "notices" }
  | { name: "onboardings" }
  | { name: "offboardings" }
  | { name: "start" }
  | { name: "leave"; employeeId?: string; trigger?: string }
  /** `offboarding` puts it under /offboardings; absent, it is under /onboardings, as every old link is. */
  | { name: "board"; id: string; kind?: "offboarding" }
  | { name: "audit"; id: string; kind?: "offboarding" }
  /** One piece of work as a condensed record to print or save as a PDF: .../summary. */
  | { name: "summary"; id: string; kind?: "offboarding" }
  /** The whole organisation's log, searched: /audit. */
  | { name: "log" }
  /** The organisation's data, taken away (PRD 13.4): /export. */
  | { name: "export" }
  | { name: "card"; id: string; key: string }
  | { name: "week" }
  | { name: "employees" }
  /** Every late card across the organisation, and each site's work in progress (PRD 5.4). */
  | { name: "overdue" }
  /** The setup wizard: a new organisation to its first onboarding (Stage 5). */
  | { name: "setup" }
  /** Self-serve signup: make an organisation (the website's buttons land here). */
  | { name: "signup" }
  | { name: "employee"; id: string }
  | { name: "people" }
  | { name: "settings" }
  | { name: "preview" }
  | { name: "templates" }
  /** `tab: "preview"` opens it on the preview, which changes nothing: /templates/:id/preview. */
  | { name: "template"; id: string; tab?: "preview" }
  | { name: "admin" }
  | { name: "missing" };

const decode = (value: string): string | null => {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
};

export function screenAt(pathname: string): Screen {
  const parts = pathname.replace(/\/+$/, "").split("/").filter(Boolean).map(decode);
  if (parts.some((part) => part === null)) return { name: "missing" };
  const [first, second, third, fourth] = parts as string[];

  if (!first) return { name: "queue" };
  if (parts.length === 1) {
    switch (first) {
      case "notices":
      case "onboardings":
      case "offboardings":
      case "week":
      case "employees":
      case "people":
      case "settings":
      case "preview":
      case "admin":
      case "templates":
      case "overdue":
      case "setup":
      case "signup":
        return { name: first };
      case "audit":
        return { name: "log" };
      case "export":
        return { name: "export" };
      // The sign-in link's path: by the time anything is drawn here, the token has been read.
      case "signin":
        return { name: "queue" };
    }
  }
  if (first === "onboardings" && second) {
    if (second === "new" && parts.length === 2) return { name: "start" };
    if (parts.length === 2) return { name: "board", id: second };
    if (third === "audit" && parts.length === 3) return { name: "audit", id: second };
    if (third === "summary" && parts.length === 3) return { name: "summary", id: second };
    if (third === "cards" && fourth && parts.length === 4) return { name: "card", id: second, key: fourth };
  }
  if (first === "offboardings" && second) {
    if (second === "new" && parts.length === 2) return { name: "leave" };
    if (parts.length === 2) return { name: "board", id: second, kind: "offboarding" };
    if (third === "audit" && parts.length === 3) return { name: "audit", id: second, kind: "offboarding" };
    if (third === "summary" && parts.length === 3)
      return { name: "summary", id: second, kind: "offboarding" };
  }
  if (first === "employees" && second && parts.length === 2) return { name: "employee", id: second };
  if (first === "templates" && second && parts.length === 2) return { name: "template", id: second };
  if (first === "templates" && second && third === "preview" && parts.length === 3)
    return { name: "template", id: second, tab: "preview" };
  if (first === "employees" && second && third === "leave" && parts.length === 3)
    return { name: "leave", employeeId: second };
  if (first === "employees" && second && third === "leave" && fourth && parts.length === 4)
    return { name: "leave", employeeId: second, trigger: fourth };
  return { name: "missing" };
}

export function pathOf(screen: Screen): string {
  const id = "id" in screen ? encodeURIComponent(screen.id) : "";
  switch (screen.name) {
    case "queue":
    case "missing":
      return "/";
    case "start":
      return "/onboardings/new";
    case "leave":
      if (screen.employeeId && screen.trigger)
        return `/employees/${encodeURIComponent(screen.employeeId)}/leave/${encodeURIComponent(screen.trigger)}`;
      return screen.employeeId
        ? `/employees/${encodeURIComponent(screen.employeeId)}/leave`
        : "/offboardings/new";
    case "board":
      return `/${screen.kind ? "offboardings" : "onboardings"}/${id}`;
    case "audit":
      return `/${screen.kind ? "offboardings" : "onboardings"}/${id}/audit`;
    case "summary":
      return `/${screen.kind ? "offboardings" : "onboardings"}/${id}/summary`;
    case "log":
      return "/audit";
    case "card":
      return `/onboardings/${id}/cards/${encodeURIComponent(screen.key)}`;
    case "employee":
      return `/employees/${id}`;
    case "template":
      return `/templates/${id}${screen.tab ? `/${screen.tab}` : ""}`;
    default:
      return `/${screen.name}`;
  }
}

/** One onboarding's or offboarding's board, at the address of its own kind. */
export const boardOf = (id: string, kind: LifecycleKind): Screen =>
  kind === "offboarding" ? { name: "board", id, kind } : { name: "board", id };

export const auditOf = (id: string, kind: LifecycleKind): Screen =>
  kind === "offboarding" ? { name: "audit", id, kind } : { name: "audit", id };

export const summaryOf = (id: string, kind: LifecycleKind): Screen =>
  kind === "offboarding" ? { name: "summary", id, kind } : { name: "summary", id };

const listeners = new Set<() => void>();

/** Move to a screen, keeping the history. */
export function go(screen: Screen | string, replace = false): void {
  const path = typeof screen === "string" ? screen : pathOf(screen);
  if (replace) window.history.replaceState(null, "", path);
  else window.history.pushState(null, "", path);
  window.scrollTo(0, 0);
  for (const listener of listeners) listener();
}

export function useLocation(): Screen {
  const [screen, setScreen] = useState(() => screenAt(window.location.pathname));
  useEffect(() => {
    const update = () => setScreen(screenAt(window.location.pathname));
    listeners.add(update);
    window.addEventListener("popstate", update);
    return () => {
      listeners.delete(update);
      window.removeEventListener("popstate", update);
    };
  }, []);
  return screen;
}

/** An ordinary link's click, kept inside the page; a modified click still opens a tab. */
export const follow = (screen: Screen) => (event: MouseEvent<HTMLAnchorElement>) => {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
  event.preventDefault();
  go(screen);
};
